import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Icon from "../../components/AppIcon";
import Image from "../../components/AppImage";
import * as URLS from "../../urls";
import axios from "axios";
import { toast } from "react-toastify";

const AdminLoginAuthentication = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState("ar");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("language") || "ar";
    setLanguage(savedLanguage);
    document.documentElement.dir = savedLanguage === "ar" ? "rtl" : "ltr";
  }, []);

  const labels = {
    title: { en: "Admin Login", ar: "تسجيل دخول المسؤول" },
    subtitle: {
      en: "Secure authentication for administrative access",
      ar: "مصادقة آمنة للوصول الإداري",
    },
    usernameLabel: { en: "Username", ar: "اسم المستخدم" },
    usernamePlaceholder: { en: "Enter your username", ar: "أدخل اسم المستخدم" },
    passwordLabel: { en: "Password", ar: "كلمة المرور" },
    passwordPlaceholder: { en: "Enter your password", ar: "أدخل كلمة المرور" },
    login: { en: "Login", ar: "تسجيل الدخول" },
    invalidUsername: {
      en: "Please enter a valid username",
      ar: "الرجاء إدخال اسم مستخدم صحيح",
    },
    invalidPassword: {
      en: "Password must be at least 6 characters",
      ar: "يجب أن تكون كلمة المرور 6 أحرف على الأقل",
    },
    switchLanguage: { en: "العربية", ar: "English" },
  };

  const toggleLanguage = () => {
    const newLanguage = language === "en" ? "ar" : "en";
    setLanguage(newLanguage);
    localStorage.setItem("language", newLanguage);
    document.documentElement.dir = newLanguage === "ar" ? "rtl" : "ltr";
  };

  const validateForm = () => {
    const newErrors = {};

    if (!username || username?.trim()?.length < 3) {
      newErrors.username = labels?.invalidUsername?.[language];
    }

    if (!password || password?.length < 6) {
      newErrors.password = labels?.invalidPassword?.[language];
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleLogin = async (e) => {
    e?.preventDefault();

    setLoading(true);
    if (!validateForm()) return;
    try {
      const response = await axios.post(`${URLS.baseUrl}/${URLS.loginURL}`, {
        phone_number: username,
        password: password,
      });

      if (response.status == "200") {
        localStorage.setItem("Token", response.data.data.token);
        localStorage.setItem(
          "user",
          JSON.stringify({
            id: response.data.data.id,
            first_name: response.data.data.first_name,
            last_name: response.data.data.last_name,
          }),
        );
        localStorage.setItem("adminAuth", "true");
        navigate("/users-management-hub");
      } else {
        response.payload?.errors?.forEach((error) => toast.warn(error));
      }
    } catch (error) {
      const errors = error?.response?.data?.errors;
      if (errors && typeof errors === "object") {
        const allErrors = Object.values(errors).flat();
        allErrors.forEach((msg) => toast.warn(msg));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background flex items-center justify-center p-4">
     {/* <button
        onClick={toggleLanguage}
        className="fixed top-4 right-4 px-4 py-2 rounded-lg bg-card border border-border hover:bg-muted transition-smooth flex items-center gap-2 shadow-md z-10"
      >
        <Icon name="Languages" size={18} />
        <span className="text-sm font-medium">
          {labels?.switchLanguage?.[language]}
        </span>
      </button>*/}

      <div className="w-full max-w-md">
        <div className="bg-card rounded-2xl shadow-elevation-4 border border-border p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Image
                src="/assets/images/logo.jpeg"
                alt="AdminPanel Logo"
                className="w-12 h-12 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {labels?.title?.[language]}
            </h1>
            <p className="text-sm text-text-secondary">
              {labels?.subtitle?.[language]}
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                {labels?.usernameLabel?.[language]}
                <span className="text-error ml-1">*</span>
              </label>
              <Input
                type="text"
                placeholder={labels?.usernamePlaceholder?.[language]}
                value={username}
                onChange={(e) => {
                  setUsername(e?.target?.value);
                  if (errors?.username) {
                    setErrors({ ...errors, username: "" });
                  }
                }}
                error={errors?.username}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                {labels?.passwordLabel?.[language]}
                <span className="text-error ml-1">*</span>
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={labels?.passwordPlaceholder?.[language]}
                  value={password}
                  onChange={(e) => {
                    setPassword(e?.target?.value);
                    if (errors?.password) {
                      setErrors({ ...errors, password: "" });
                    }
                  }}
                  error={errors?.password}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute top-1/2 -translate-y-1/2 ltr:right-3 rtl:left-3 text-text-secondary hover:text-foreground transition-smooth"
                >
                  <Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
                </button>
              </div>
            </div>

            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={loading}
              iconName="LogIn"
            >
              {labels?.login?.[language]}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginAuthentication;
